import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { fmtMoney, fmtErr } from "../api";
import { CreditCard, Bank, CheckCircle, LockSimple } from "@phosphor-icons/react";
import { LegalLinks } from "../components/LegalLinks";
import { toast } from "sonner";

export default function PayLink() {
  const { token } = useParams();
  const [params, setParams] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);
  const [checking, setChecking] = useState(false);
  const [paid, setPaid] = useState(false);
  const polled = useRef(false);

  const load = () => api.get(`/public/pay/${token}`).then((r) => setInv(r.data)).catch((e) => setErr(e.response?.data?.detail || "This payment link isn't valid"));

  useEffect(() => {
    load();
    const sid = params.get("session_id");
    if (sid && !polled.current) {
      polled.current = true;
      setChecking(true);
      const t = setInterval(async () => {
        try {
          const r = await api.get(`/payments/status/${sid}`);
          if (r.data.payment_status === "paid") {
            clearInterval(t);
            setChecking(false);
            setPaid(true);
            toast.success("Payment received — thank you!");
            setParams({});
            load().catch(() => {}); // link self-destructs after payment — success card below is driven by `paid`
          }
        } catch { /* payment poll retry */ }
      }, 2000);
      setTimeout(() => { clearInterval(t); setChecking(false); }, 30000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load + payment poll by design
  }, [token]);

  const payCard = async () => {
    try {
      const r = await api.post(`/public/pay/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-4 py-8" data-testid="pay-link-page">
      <div className="w-full max-w-lg space-y-4">
        {inv?.logo_url && <img src={inv.logo_url} alt={inv.business_name || ""} className="mx-auto max-h-14" data-testid="pay-link-logo" />}
        <p className="text-center font-heading text-xl font-bold" data-testid="pay-link-business">{inv?.business_name || "Invoice"}</p>

        {err && !paid && (
          <div className="card-flat space-y-2 p-8 text-center" data-testid="pay-link-error">
            <LockSimple size={40} weight="bold" className="mx-auto text-muted-foreground" />
            <p className="font-heading text-lg font-bold">Link unavailable</p>
            <p className="text-sm text-muted-foreground">{err}</p>
          </div>
        )}

        {!err && !inv && <p className="text-center text-muted-foreground">Loading…</p>}

        {(paid || (inv && inv.status === "paid")) && (
          <div className="card-flat space-y-3 p-8 text-center" data-testid="payment-success">
            <CheckCircle size={56} weight="bold" className="mx-auto text-primary" />
            <p className="font-heading text-2xl font-bold">Paid — thank you</p>
            <p className="text-sm text-muted-foreground">{inv.number} · {fmtMoney(inv.total)}{inv.payment_method ? ` via ${inv.payment_method === "card" ? "card" : inv.payment_method}` : ""}</p>
          </div>
        )}

        {!paid && inv && inv.status === "cancelled" && (
          <div className="card-flat space-y-2 p-8 text-center" data-testid="pay-link-cancelled">
            <p className="font-heading text-lg font-bold">This invoice was cancelled</p>
            <p className="text-sm text-muted-foreground">{inv.number}{inv.cancel_reason ? ` — ${inv.cancel_reason}` : ""}</p>
            <p className="text-xs text-muted-foreground">Nothing is owed. Questions? Contact {inv.business_name}.</p>
          </div>
        )}

        {!paid && inv && !["paid", "cancelled"].includes(inv.status) && (
          <>
            <div className="card-flat p-6">
              <p className="label-caps">{inv.business_name}</p>
              <p className="mt-1 font-heading text-lg font-bold">{inv.property_name}</p>
              <div className="mt-3 space-y-1">
                {inv.line_items.map((l, i) => (
                  <div key={i} className="flex justify-between text-sm"><span className="text-muted-foreground">{l.description}</span><span>{fmtMoney(l.amount)}</span></div>
                ))}
              </div>
              <div className="mt-4 space-y-1 border-t border-border pt-4">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span>{fmtMoney(inv.subtotal ?? inv.total)}</span></div>
                {!!inv.credits_total && <div className="flex justify-between text-sm" data-testid="credit-line"><span className="text-muted-foreground">Credits</span><span>{fmtMoney(inv.credits_total)}</span></div>}
                {!!inv.credits_total && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Taxable amount</span><span>{fmtMoney(inv.taxable_base ?? (inv.subtotal + inv.credits_total))}</span></div>}
                {inv.tax_amount > 0 && <div className="flex justify-between text-sm" data-testid="tax-line"><span className="text-muted-foreground">{inv.tax_label_used || "HST"} ({inv.tax_rate_used}%)</span><span>{fmtMoney(inv.tax_amount)}</span></div>}
                <div className="flex justify-between pt-1">
                  <span className="label-caps">Total due</span>
                  <span className="font-heading text-3xl font-bold" data-testid="amount-due">{fmtMoney(inv.total)}</span>
                </div>
                {inv.tax_number_used && <p className="text-xs text-muted-foreground">{inv.tax_label_used || "HST"} # {inv.tax_number_used}</p>}
              </div>
            </div>

            {inv.receipts?.length > 0 && (
              <div className="card-flat space-y-2 p-5" data-testid="receipt-photos">
                <p className="label-caps">Receipts</p>
                <div className="flex flex-wrap gap-3">
                  {inv.receipts.map((r, i) => (
                    <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${r.url}`} target="_blank" rel="noopener noreferrer" data-testid={`receipt-${i}`}>
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${r.url}`} alt={r.description} className="h-24 rounded-md object-cover" />
                      <p className="mt-1 text-xs text-muted-foreground">{r.description} · {fmtMoney(r.amount)}</p>
                    </a>
                  ))}
                </div>
              </div>
            )}

            <p className="label-caps">How do you want to pay?</p>
            {inv.card_available && (
              <button data-testid="pay-card-btn" onClick={payCard} disabled={checking}
                className="card-flat flex w-full items-center gap-4 p-5 text-left transition-colors hover:bg-muted/50 disabled:opacity-50">
                <CreditCard size={28} weight="bold" className="text-primary" />
                <div className="flex-1">
                  <p className="font-bold">Credit / debit card</p>
                  <p className="text-sm text-muted-foreground">Instant{inv.card_fee ? ` · ~${fmtMoney(inv.card_fee)} card processing cost` : ""}</p>
                </div>
                <span className="font-heading font-bold">{fmtMoney(inv.total)}</span>
              </button>
            )}
            {checking && <p className="text-center text-sm text-muted-foreground" data-testid="payment-checking">Checking your payment…</p>}
            {inv.etransfer_email && (
              <div className="card-flat space-y-2 p-5" data-testid="etransfer-instructions">
                <div className="flex items-center gap-3">
                  <Bank size={28} weight="bold" className="text-primary" />
                  <p className="font-bold">Interac e-Transfer</p>
                </div>
                <p className="text-sm">Send <span className="font-bold">{fmtMoney(inv.total)}</span> to:</p>
                <p className="rounded-md bg-muted p-3 font-mono text-sm font-bold" data-testid="etransfer-email">{inv.etransfer_email}</p>
                <p className="text-sm">Reference / message: <span className="font-bold" data-testid="etransfer-ref">{inv.number}</span></p>
                <p className="text-xs text-muted-foreground">Free · confirmed manually (1 business day)</p>
              </div>
            )}
          </>
        )}

        <p className="pt-4 text-center text-xs text-muted-foreground">Secure payment link · Powered by RPM Field</p>
        <LegalLinks />
      </div>
    </div>
  );
}
